// form
import { useFormContext, Controller } from 'react-hook-form';
// @mui
import { TextField, TextFieldProps } from '@mui/material';
import { useCallback } from 'react';
import { InputAdornment } from '@mui/material';
import { IconButton } from '@mui/material';
import Iconify from 'src/components/Iconify';
import { useSnackbar } from 'notistack';

// ----------------------------------------------------------------------

type IProps = {
  name: string;
  enableContactPicker?: boolean; // optional prop
};

type Props = IProps & TextFieldProps;

export default function RHFTextField({ name, enableContactPicker = false, ...other }: Props) {
  const { control, setValue } = useFormContext();
  const { enqueueSnackbar } = useSnackbar();

  const handlePickContact = useCallback(async () => {
    // Use 'any' to bypass TypeScript's lack of support for the experimental API
    const nav: any = navigator;
    if (nav.contacts && typeof nav.contacts.select === 'function') {
      try {
        const props = ['tel']; // Only request phone numbers
        const opts = { multiple: false };
        const contacts = await nav.contacts.select(props, opts);
        if (contacts && contacts.length > 0) {
          const contact = contacts[0];
          let value = '';
          if (contact.tel && contact.tel.length > 0) {
            value = contact.tel[0];
          }
          if (value) {
            setValue(name, value, { shouldValidate: true });
          } else {
            enqueueSnackbar('No phone number found in selected contact.', { variant: 'warning' });
          }
        } else {
          enqueueSnackbar('No contact selected.', { variant: 'info' });
        }
      } catch (error) {
        enqueueSnackbar('Contact selection was cancelled or failed.', { variant: 'error' });
      }
    } else {
      enqueueSnackbar('Contact Picker is not supported on this device/browser.', { variant: 'warning' });
    }
  }, [name, setValue, enqueueSnackbar]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          fullWidth
          error={!!error}
          helperText={error?.message}
          InputProps={{
            ...(enableContactPicker && {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handlePickContact} edge="end">
                    <Iconify icon="ic:baseline-contacts" />
                  </IconButton>
                </InputAdornment>
              ),
            }),
            ...other.InputProps,
          }}
          {...other}
        />
      )}
    />
  );
}
